import { getSessionStorageService } from "@/infrastructure/di/services";
import { reloadPage } from "@/infrastructure/navigation";
import { SESSION_STORAGE_KEYS } from "@/infrastructure/storage/constants";

/**
 * Version-skew detection: an open tab can run a bundle deployed days ago
 * against an API whose contract has moved on. The deploy pipeline publishes
 * `version.json` (same origin, no-cache) with the deployed commit; this
 * module compares it against the bundle's own version and reloads the tab
 * at moments the user won't notice — never via a prompt.
 */

const VERSION_ENDPOINT = "/version.json";
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const HIDDEN_RELOAD_DELAY_MS = 10 * 60 * 1000;
const CONTRACT_RELOAD_GUARD_MS = 5 * 60 * 1000;

let updatePending = false;
let hiddenReloadTimer: number | undefined;

export function getCurrentVersion(): string {
  return import.meta.env.VITE_APP_VERSION ?? "dev";
}

export async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(VERSION_ENDPOINT, { cache: "no-store" });
    if (!response.ok) return null;
    const body = (await response.json()) as { version?: unknown };
    return typeof body.version === "string" ? body.version : null;
  } catch {
    return null;
  }
}

export function isUpdatePending(): boolean {
  return updatePending;
}

/**
 * Compares the deployed version against this bundle's. Sticky: once an
 * update is pending it stays pending until the reload happens. Network
 * failures never mark the bundle stale.
 */
export async function checkForUpdate(): Promise<boolean> {
  if (updatePending) return true;
  const current = getCurrentVersion();
  if (current === "dev") return false;
  const deployed = await fetchDeployedVersion();
  updatePending = deployed !== null && deployed !== current;
  return updatePending;
}

/**
 * Reload now if a new version is pending. Wired to route navigations: the
 * target page remounts anyway, so a hard reload there is invisible.
 */
export function reloadIfUpdatePending(): boolean {
  if (!updatePending) return false;
  reloadPage();
  return true;
}

/**
 * A ContractViolationError while the bundle is stale is version skew, not
 * spec drift: reload into the deployed bundle instead of leaving the user on
 * broken code. The sessionStorage guard prevents reload loops when the
 * violation persists after a fresh load (genuine drift).
 */
export async function reloadOnContractViolationSkew(): Promise<void> {
  if (!(await checkForUpdate())) return;
  const storage = getSessionStorageService();
  const lastReloadAt = Number(storage.get<string>(SESSION_STORAGE_KEYS.SKEW_RELOADED_AT) ?? 0);
  if (Date.now() - lastReloadAt < CONTRACT_RELOAD_GUARD_MS) return;
  storage.set(SESSION_STORAGE_KEYS.SKEW_RELOADED_AT, String(Date.now()));
  reloadPage();
}

/**
 * Starts the watch: re-check when the tab regains focus and on an interval.
 * When an update is pending and the tab goes to the background, reload after
 * a grace period so a quick tab switch mid-form never loses work.
 * Returns a teardown that stops the watch.
 */
export function initVersionSkewWatch(): () => void {
  if (getCurrentVersion() === "dev") return () => {};

  const interval = window.setInterval(() => void checkForUpdate(), CHECK_INTERVAL_MS);

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      window.clearTimeout(hiddenReloadTimer);
      void checkForUpdate();
    } else if (updatePending) {
      hiddenReloadTimer = window.setTimeout(() => {
        if (document.visibilityState === "hidden") reloadPage();
      }, HIDDEN_RELOAD_DELAY_MS);
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    window.clearInterval(interval);
    window.clearTimeout(hiddenReloadTimer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
